import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS } from "@tSurya/theme/design";
import React, { FC, PropsWithChildren, ReactNode } from "react";
import { css } from "styled-components";

export interface RadioCardStyles {
    wrapper?: InjectableCss;
}

export interface RadioCardProps extends PropsWithChildren {
    value: string;
    isSelected?: boolean;
    isDisabled?: boolean;
    onSelect: (value: string) => void;
    onClickChild?: () => void;
    excludeClickSelectors?: string;
    extendedStyles?: RadioCardStyles;
    className?: string;
    children?: ReactNode;
}

export const radioCardStyles: RadioCardStyles = {
    wrapper: {
        css: css`
            width: 100%;
            padding: 12px 16px;
            border: 1px solid ${COLORS.border.main};
            border-radius: 4px;
            background: ${COLORS.white};
            transition: border-color 0.2s ease;
            cursor: pointer;

            &:hover {
                border-color: ${COLORS.primary.base};
            }

            /* Shared radio button geometry - outer circle 20px, inner circle 10px centered */
            input {
                margin-left: 0 !important;
            }
            input + label::before {
                height: 20px !important;
                width: 20px !important;
            }
            input:checked + label::after {
                height: 10px !important;
                width: 10px !important;
                left: 10px !important;
                top: 50% !important;
                transform: translate(-50%, -50%) !important;
            }
        `,
    },
};

const RadioCard: FC<RadioCardProps> = ({
    value,
    isSelected,
    isDisabled,
    onSelect,
    onClickChild,
    excludeClickSelectors = "input, label",
    extendedStyles,
    className,
    children,
}) => {
    const styles = {
        ...radioCardStyles,
        ...extendedStyles,
    };

    const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
        if (isDisabled) {
            return;
        }

        const target = event.target as HTMLElement;
        if (target.closest(excludeClickSelectors)) {
            return;
        }

        if (onClickChild) {
            onClickChild();
            return;
        }

        onSelect(value);
    };

    return (
        <StyledWrapper
            css={css`
                ${styles.wrapper?.css}
                ${isSelected &&
                css`
                    border-color: ${COLORS.primary.base};
                    cursor: default;
                `}
                ${isDisabled &&
                css`
                    cursor: not-allowed;
                `}
            `}
            className={className}
            onClick={handleClick}
        >
            {children}
        </StyledWrapper>
    );
};

export default RadioCard;
